<template>
    <div class="input-wrapper-custom-material">
        <div class="group">
            <imask-input
                ref="imaskInput"
                v-model="displayValue"
                :mask="Number"
                :thousandsSeparator="userThouSep"
                :scale="scale"
                :radix="userDecSep"
                :unmask="true"
                class="mdl-textfield__input"
                :mapToRadix="[userDecSep]"
                placeholder=" "
                @change="handleValueChange"
                :max="maxValue ? maxValue : undefined"
                :min="minValue ? minValue : undefined"
                :normalizeZeros="true"
                :class="{ 'is-invalid': errorMessages.length > 0}"
            />
            <span class="highlight"></span>
            <span class="bar" :class="{ 'is-invalid': errorMessages.length > 0}"></span>
            <label
                v-if="fieldTextTranslation"
                class="mdl-textfield__label v-label theme--light"
                :class="{ 'is-invalid': errorMessages.length > 0}"
            >
                {{ $t(fieldTextTranslation) }}
            </label>
            <div v-if="errorMessages.length > 0" class="text-field__error pt-1">
                {{ errorMessages[0] }}
            </div>
        </div>
    </div>
</template>
<script>
import { IMaskComponent } from 'vue-imask';
import { NUMERIC_MAX_VALUE } from '@/ecm/common/Constants';
import Utils from '@/ecm/common/Utils';
export default {
    props: {
        fieldValue: {
            type: [String, Number],
            default: ''
        },
        fieldTextTranslation: {
            type: String,
            default: ''
        },
        scale: {
            type: Number
        },
        maxValue: {
            type: Number,
            default: NUMERIC_MAX_VALUE
        },
        minValue: {
            type: Number
        },
        errorMessages: {
            type: Array,
            default: () => []
        },
        userThouSep: {
            type: String,
            default: ','
        },
        userDecSep: {
            type: String,
            default: '.'
        }
    },
    components: {
        'imask-input': IMaskComponent,
    },
    data() {
        return {
            displayValue: Utils.number.toUserFormat(this.fieldValue, this.userDecSep, this.userThouSep).toString(),
        };
    },
    methods: {
        handleValueChange() {
            if (this.displayValue !== '' && !isNaN(parseFloat(this.displayValue))) {
                const scale = parseInt(this.scale || 0);

                let numberStr = this.displayValue;

                const maxVal = this.maxValue;
                const minVal = this.minValue;

                let number = parseFloat(numberStr);

                if (!isNaN(maxVal) && number > maxVal) {
                    number = maxVal;
                    numberStr = maxVal.toString();
                }

                if (!isNaN(minVal) && number < minVal) {
                    number = minVal;
                    numberStr = minVal.toString();
                }

                const parts = numberStr.split('.');
                const integerPart = parts[0];
                let decimalPart = parts.length > 1 ? parts[1] : '';

                if (decimalPart.length > scale) {
                    decimalPart = decimalPart.substring(0, scale);
                } else if (decimalPart.length < scale) {
                    decimalPart = decimalPart.padEnd(scale, '0');
                }

                const formattedValue = scale > 0
                    ? `${integerPart}.${decimalPart}`
                    : integerPart;

                this.$emit('onValueChanged', formattedValue);
                this.displayValue = Utils.number.toUserFormat(this.fieldValue, this.userDecSep, this.userThouSep);
            } else if (this.displayValue === '') {
                this.$emit('onValueChanged', '');
            }
        },
    },
    watch: {
        scale() {
            if (this.displayValue === '') return;

            const value = this.fieldValue.toString();
            const parts = value.split('.');
            const integerPart = parts[0];
            let decimalPart = parts.length > 1 ? parts[1] : '';

            if (decimalPart.length > this.scale) {
                decimalPart = decimalPart.substring(0, this.scale);
            } else if (decimalPart.length < this.scale) {
                decimalPart = decimalPart.padEnd(this.scale, '0');
            }

            const formattedValue = this.scale > 0
                ? `${integerPart}.${decimalPart}`
                : integerPart;

            this.displayValue = Utils.number.toUserFormat(this.fieldValue, this.userDecSep, this.userThouSep);
            this.$emit('onValueChanged', formattedValue);

        },
        fieldValue() {
            this.displayValue = Utils.number.toUserFormat(this.fieldValue, this.userDecSep, this.userThouSep);
        }
    }
};
</script>
<style lang="scss" scoped>
$bg-color: #424242;
$hl-color: #00cae3;
$muted-color: mix(white, $bg-color, 70%);
$unfocused-color: rgb(118, 118, 118);
$invalid-color: rgb(255, 87, 86);
$trans-time: 300ms;
$width: 100%;

.input-wrapper-custom-material {
  position: relative;
  display: flex;
  align-items: center;
  .group {
    flex: 1;
    position: relative;
    margin: 14px 0 10px 0;
  }
  // INPUTS
  .mdl-textfield__input {
    background: none;
    color: inherit;
    accent-color: #00cae3;
    line-height: 12px;
    font-size: 14px;
    padding: 8px 0;
    display: block;
    width: $width;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid $muted-color;
    &:focus {
      outline: none;
    }
    &:required {
      &:focus ~ label,
      &:valid ~ label {
        top: -10px;
        font-size: 10px;
        color: $hl-color;
      }
      &:not(:focus) ~ label {
        color: $unfocused-color;
      }
      &:invalid.dirty ~ label {
        color: $invalid-color;
      }
    }
    &:not(:required) {
      &:focus ~ label,
      &:not(:placeholder-shown):valid ~ label {
        top: -10px;
        font-size: 10px;
        color: $hl-color;
      }
      &:not(:focus) ~ label,
      &:not(:placeholder-shown):valid:not(:focus) ~ label {
        color: $unfocused-color;
      }
    }
    &:focus ~ .bar:before {
      width: $width;
    }
  }
  label {
    font-size: 14px;
    position: absolute;
    pointer-events: none;
    top: 10px;
    transition: $trans-time ease all;
    color: $unfocused-color;
    font-weight: 300;
  }
  .bar {
    position: relative;
    display: block;
    width: $width;
    &:before {
      content: "";
      height: 2px;
      width: 0;
      bottom: 0px;
      position: absolute;
      background: $hl-color;
      transition: $trans-time ease all;
      left: 0%;
    }
    &.is-invalid:before {
      background: $invalid-color;
    }
  }
  .text-field__error {
    color: $invalid-color;
    font-size: 12px;
    line-height: 12px;
    word-break: break-word;
    word-wrap: break-word;
    position: absolute;
    top: 100%;
  }
  .is-invalid {
    color: $invalid-color;
  }
}
</style>
